import useOverlay from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import Alert from "@COMPONENTS/common/modal/alert/Desktop";
import {GalleryType, ReviewThumbnail} from "@COMPONENTS/product/pages/detailPackage/desktop/schedule/Review";
import * as style from "@COMPONENTS/style/detailpackage/reviewpopup";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import {PopupCloseButton} from "@FRAMEWORK/ui/desktop/button";
import {StarScore} from "@FRAMEWORK/ui/desktop/flag";
import {Flex} from "@FRAMEWORK/ui/desktop/layout";
import useSigninUser from "@QUERIES/account/useSigninUser";
import useReview, {AttachType} from "@QUERIES/portal/useReview";
import {reviewLikeAsync} from "@QUERIES/portal/useReviewLike";
import useReviewLikeRecommender from "@QUERIES/portal/useReviewLikeRecommender";
import useReviewPictures from "@QUERIES/support/useReviewPictures";
import {Noimage} from "@yb/ui/desktop/common/Noimage";
import dayjs from "dayjs";
import {Fragment, useEffect, useMemo, useState} from "react";

type Props = {goodsCd: string; reviewId: number; close: () => void; listOpen: () => void; optionAttach?: AttachType};

const ReviewImageVideo = (attach: AttachType) => {
	const {sevSaveDirNm, sevSaveFileNm, vimeoEndPoint} = attach;
	// 이미지 리턴
	const imageUrl =
		sevSaveDirNm && sevSaveFileNm ? `${publicRuntimeConfig.ENV_SCDN_URL}${sevSaveDirNm}/${sevSaveFileNm}` : null;

	const vimeoId = vimeoEndPoint?.split("/")[2] || null;
	const [vimeoUrl, setVimeoUrl] = useState(null);

	const reviewPictruesFetch = useReviewPictures(vimeoId, {enabled: !!vimeoId});
	const reviewPictrues = reviewPictruesFetch.data?.body || null;

	useEffect(() => {
		if (reviewPictrues) setVimeoUrl(reviewPictrues.player_embed_url);
	}, [reviewPictrues]);

	return (
		<>
			{vimeoEndPoint && vimeoUrl ? (
				<iframe src={vimeoUrl} allow="autoplay; fullscreen" width="100%" height="100%" frameBorder="0" />
			) : (
				<Noimage src={imageUrl} alt="" />
			)}
		</>
	);
};

const ReviewPopupComponent = (props: Props) => {
	const {goodsCd, reviewId, close, listOpen, optionAttach = null} = props;

	const [open] = useOverlay();
	const [currentId, setCurrentId] = useState(reviewId);
	// 선택된 이미지 default[0]
	const [selectedAttach, setSelectedAttach] = useState<AttachType>(optionAttach);

	// 유저 정보 조회
	const signin = useSigninUser();
	const signinCode = signin.data?.code || "9999";

	// 리뷰 리스트 조회
	const reviewDataFetch = useReview(goodsCd);
	const reviewData = reviewDataFetch.data?.body || null;

	// 리뷰 좋아요 조회 (로그인 유저)
	const reviewLikeRecommenderFetch = useReviewLikeRecommender(goodsCd, {enabled: signinCode === "0000"});
	const reviewLikeRecommender = reviewLikeRecommenderFetch.data?.body || [];

	const originReview = useMemo(() => {
		const {reviewList, attachList: attach} = reviewData;
		return reviewList.reduce((acc, cur) => {
			const attachList = attach.filter(v => v.reviewId === cur.reviewId) || null;
			const item = {...cur, attachList};
			acc.push(item);
			return acc;
		}, []);
	}, [reviewData]);

	/** 사진&동영상 리스트 */
	const galleries = useMemo(() => {
		if (!originReview) return [] as GalleryType[];
		const filteredReview = originReview.filter(v => v.attachList?.length > 0);
		const result = filteredReview.reduce((acc, cur, idx, src) => {
			const prevReviewId = src[idx - 1]?.reviewId || null;
			const nextReviewId = src[idx + 1]?.reviewId || null;
			const item = {...cur, prevReviewId, nextReviewId};
			acc.push(item);
			return acc;
		}, [] as GalleryType[]);
		return result;
	}, [originReview]);

	const gallery = useMemo(() => galleries.find(v => v.reviewId === currentId), [currentId, galleries]);
	const attachList = gallery.attachList ?? [];

	useEffect(() => {
		if (!selectedAttach) setSelectedAttach(attachList[0]);
	}, []);

	// 이전 리뷰
	const handlePrev = () => {
		setCurrentId(gallery.prevReviewId);
		const currentList = galleries.find(v => v.reviewId === gallery.prevReviewId);
		if (currentList) setSelectedAttach(currentList.attachList[0]);
	};
	// 다음 리뷰
	const handleNext = () => {
		setCurrentId(gallery.nextReviewId);
		const currentList = galleries.find(v => v.reviewId === gallery.nextReviewId);
		if (currentList) setSelectedAttach(currentList.attachList[0]);
	};

	// 이미지 클릭 이벤트
	const handleImageClick = (attach: AttachType) => setSelectedAttach(attach);

	// 추천하기(좋아요) 버튼
	const handleLike = (reviewId: number) => {
		if (signinCode === "0000") {
			reviewLikeAsync({goodsCd, reviewId: reviewId.toString()}).then(result => {
				if (result.data.code === "0000") {
					reviewLikeRecommenderFetch.refetch();
					reviewDataFetch.refetch();
				}
			});
		} else {
			open(prop => (
				<Alert
					{...prop}
					alertType="confirm"
					text="로그인 후 이용하실 수 있습니다. <br/>로그인하시겠습니까?"
					afterConfirm={() => LoginUtil.login()}
				/>
			));
		}
	};

	// 스크롤 제거
	useEffect(() => {
		document.body.style.overflowY = "hidden";
		return () => {
			document.body.style.removeProperty("overflow-y");
		};
	}, []);

	return (
		<style.Wrap>
			<style.Popup>
				<style.Header line={false}>
					<Flex gap="20" align="end">
						<h3>사진&amp;동영상</h3>
						<style.ListLink
							href="#"
							onClick={e => {
								e.preventDefault();
								listOpen();
								close();
							}}
						>
							<span>전체보기</span>
						</style.ListLink>
					</Flex>
					<PopupCloseButton onClose={close} />
				</style.Header>
				<style.DetailContent>
					<style.Media>
						{/* 이미지, 영상 */}
						<ReviewImageVideo {...selectedAttach} />
					</style.Media>
					<style.ReviewContent>
						<style.PreviewList>
							{attachList.map((attach, index) => (
								<style.PreviewItem
									key={index}
									onClick={() => handleImageClick(attach)}
									$isSelected={selectedAttach === attach}
								>
									<ReviewThumbnail {...attach} />
								</style.PreviewItem>
							))}
						</style.PreviewList>
						<style.ReviewInfo>
							<style.ReviewTopBar>
								<style.ScoreWrap>
									<StarScore>
										<span style={{width: `calc(${gallery.score}*20%)`}} />
									</StarScore>
									<em>{gallery.score}</em>
								</style.ScoreWrap>
								<style.ReviewerProfile>
									<style.UserName>{gallery.custNm}님</style.UserName>
									<style.ProfileDetails>
										{gallery.ageNm && <span>{gallery.ageNm}</span>}
										<span>{gallery.companionNm}</span>
									</style.ProfileDetails>
								</style.ReviewerProfile>
								<style.DetailDate dateTime={dayjs(gallery.reviewDt).format("YYYY.MM.DD")}>
									{dayjs(gallery.reviewDt).format("YYYY.MM.DD")}
								</style.DetailDate>
							</style.ReviewTopBar>
							<style.Highlights>
								{gallery.keywords &&
									gallery.keywords
										.split(",")
										.map((keyword: string, index: number) => (
											<Fragment key={index}>{keyword && <style.HighlightItem>{keyword}</style.HighlightItem>}</Fragment>
										))}
							</style.Highlights>
							<style.ReviewText dangerouslySetInnerHTML={{__html: gallery.detail.replace(/\n/g, "<br />")}} />
							<style.FeedbackControls>
								<style.RecommendBtnWrap onClick={() => handleLike(gallery.reviewId)}>
									<style.RecommendBtnIcon
										$isActive={!!reviewLikeRecommender.find(v => v.reviewId === gallery.reviewId)}
									/>
									<style.RecommendBtn>추천</style.RecommendBtn>
									<style.LikeCount>{gallery.likeCnt}</style.LikeCount>
								</style.RecommendBtnWrap>
							</style.FeedbackControls>
						</style.ReviewInfo>
					</style.ReviewContent>
				</style.DetailContent>
				{gallery.prevReviewId && (
					<style.SlideButton direction="prev" onClick={handlePrev}>
						<span>이전 여행후기 보기</span>
					</style.SlideButton>
				)}
				{gallery.nextReviewId && (
					<style.SlideButton direction="next" onClick={handleNext}>
						<span>다음 여행후기 보기</span>
					</style.SlideButton>
				)}
			</style.Popup>
		</style.Wrap>
	);
};

const ReviewPopup = (props: Props) => (
	<ErrorBoundary>
		<ReviewPopupComponent {...props} />
	</ErrorBoundary>
);

export default ReviewPopup;
